import {
  Component,
  DestroyRef,
  inject,
  input,
  output,
  signal,
  PLATFORM_ID,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NgOptimizedImage, isPlatformBrowser } from '@angular/common';
import { toObservable } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';

import {
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  of,
  Subject,
  switchMap,
} from 'rxjs';

import { GoogleDataLayerService } from 'src/app/services';
import { SearchStaxService } from 'src/app/services/searchstax.service';
import { SpecialtyService } from 'src/app/services';

import {
  AppAutocompleteSearch,
  ACSOption,
} from 'src/app/uikit/autocomplete/autocomplete.component';
import { AppChip } from 'src/app/uikit/chips/chips.component';
import { AppToggle, ToggleOption, ToggleChange } from 'src/app/uikit/toggle/toggle.component';
import { AppButton } from 'src/app/uikit/button/button.component';
import { BrowseSpecialtiesComponent } from 'src/app/shared-components/browse-specialties/browse-specialties.component';

import { ChipItem } from 'src/app/models/home-chip.model';
import { ProgramType } from 'src/app/models/program.model';
import { SearchStaxSuggestion } from 'src/app/models/searchstax.model';

import { ProgramTypeLabelMap } from '../home.data';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-hero-search',
  imports: [
    NgOptimizedImage,
    RouterLink,
    AppToggle,
    AppButton,
    AppChip,
    AppAutocompleteSearch,
    BrowseSpecialtiesComponent,
  ],
  templateUrl: './hero-search.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './hero-search.component.scss',
})
export class HeroSearchComponent {
  /* Dependency injection */
  private readonly router = inject(Router);
  private readonly searchstax = inject(SearchStaxService);
  private readonly specialtyService = inject(SpecialtyService);
  private readonly dataLayerService = inject(GoogleDataLayerService);
  private readonly platformId = inject(PLATFORM_ID);

  /* Inputs */
  readonly selectedProgramType = input<ProgramType>('Residency');
  readonly programTypeOptions = input.required<ToggleOption[]>();
  readonly suggestedPrograms = input<ChipItem[]>([]);
  readonly suggestedProgramTitle = input('');
  readonly isLoading = input<boolean>(false);

  /* Outputs */
  readonly selectedProgramTypeChange = output<ProgramType>();

  /* Public state */
  readonly resetTypeaheadToken = signal(0);
  readonly query = signal('');
  readonly options = signal<ACSOption<SearchStaxSuggestion>[]>([]);
  readonly suggestions = signal<ACSOption<SearchStaxSuggestion>[] | null>(null);
  readonly programTypeLabelMap: Record<ProgramType, Record<string, string>> = ProgramTypeLabelMap;
  public showSpecialtyModal = false;
  private destroy$: Subject<boolean> = new Subject<boolean>();

  /* Constructor */
  constructor() {
    const destroyRef = inject(DestroyRef);

    const navEndEvents = this.router.events.pipe(filter((event) => event instanceof NavigationEnd));

    navEndEvents.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (
        isPlatformBrowser(this.platformId) &&
        localStorage.getItem('selectedProgramType') === 'Fellowship'
      ) {
        this.selectedProgramTypeChange.emit('Fellowship');
      }
    });

    const search$ = combineLatest([
      toObservable(this.query).pipe(map((q) => q.trim())),
      toObservable(this.selectedProgramType),
    ]).pipe(
      debounceTime(600),
      distinctUntilChanged((a, b) => a[0] === b[0] && a[1] === b[1]),
      switchMap(([q, programType]) => {
        if (q.length <= 2) {
          this.options.set([]);
          this.suggestions.set(null);
          return of(null);
        }

        return this.searchstax.getSuggestions(q, programType, false).pipe(
          catchError(() => {
            this.options.set([]);
            this.suggestions.set(null);
            return of(null);
          }),
        );
      }),
    );

    const sub = search$.subscribe((res) => {
      if (!res) return;
      this.options.set(res.specialties.map((s) => ({ label: s.title, value: s })));
      this.suggestions.set(
        res.programs.map((s) => ({
          label: s.title,
          value: s,
          extra: [s.specialtyTitle].filter(Boolean).join(' • '),
        })),
      );
    });

    destroyRef.onDestroy(() => sub.unsubscribe());
  }

  /* Event handlers */
  public onProgramTypeChange(e: ToggleChange): void {
    const value = e.value as ProgramType;
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('selectedProgramType', value);
    }
    this.selectedProgramTypeChange.emit(value);
    this.onCleared();
    this.resetTypeaheadToken.update((x) => x + 1);
  }

  public onTypeahead(q: string): void {
    this.query.set(q);
  }

  public onCleared(): void {
    this.query.set('');
    this.options.set([]);
    this.suggestions.set(null);
  }

  public onSelectedValue(val: SearchStaxSuggestion): void {
    this.dataLayerService.trackAnalytics({
      site_events: {
        freida_home_autocomplete_select: true,
      },
      freida_home_autocomplete_select_type: val.type,
      freida_home_autocomplete_select_title: val.title,
      freida_home_autocomplete_select_program_type: this.selectedProgramType(),
      ...(val.type === 'program'
        ? { freida_home_autocomplete_select_program_id: val.programId?.trim() }
        : { freida_home_autocomplete_select_specialty_nid: val.nid?.trim() }),
      ...(val.specialtyTitle
        ? { freida_home_autocomplete_select_specialty_title: val.specialtyTitle }
        : {}),
    });

    switch (val.type) {
      case 'program':
        this.navigateToProgramDetail(val.programId!.trim());
        break;
      case 'specialty':
        this.navigateToSpecialtyPrograms(val.nid!.trim());
        break;
    }
  }

  public onSearch(e: { search: string; selected?: SearchStaxSuggestion }): void {
    const sel = e.selected;
    if (sel) {
      this.onSelectedValue(sel);
      return;
    }
    const q = (e.search ?? '').trim();
    const programType = this.selectedProgramType();

    this.dataLayerService.trackAnalytics({
      site_events: {
        freida_home_search: true,
      },
      freida_home_search_query: q,
      freida_home_search_program_type: programType,
    });

    this.router.navigateByUrl(
      '/search/list?query=' + encodeURIComponent(q) + '&programType=' + programType,
    );
  }

  public onChipClick(chip: ChipItem): void {
    this.dataLayerService.trackAnalytics({
      site_events: {
        freida_home_chip_click: true,
      },
      freida_home_chip_label: chip.label,
      freida_home_chip_id: chip.id,
      freida_home_chip_url: chip.url,
      freida_home_chip_program_type: this.selectedProgramType(),
    });
  }

  public openSpecialtiesModal(): void {
    this.showSpecialtyModal = !this.showSpecialtyModal;
  }

  public afterModalClose(event: { modal: boolean }): void {
    this.showSpecialtyModal = event.modal;
  }

  public searchSpecialtyModal($event: { term: string }): void {
    if ($event.term !== '') {
      this.router.navigate(['program', $event.term]);
      return;
    }

    const specs = this.specialtyService.getSelectedSpecs();
    const converted = specs ? specs.map((s: any) => s.nid) : [];
    const queryParams = { spec: converted.join(','), fromHeader: true, loc: null };
    const route = this.router.url.includes('map') ? '/search/map' : '/search/list';

    this.router.navigate([route], { queryParamsHandling: 'merge', queryParams });
  }

  /* Private methods */
  private navigateToProgramDetail(programId: string): void {
    this.router.navigateByUrl('/program/' + programId);
  }

  private navigateToSpecialtyPrograms(nid: string): void {
    this.router.navigateByUrl('/search/list?spec=' + nid);
  }
}
